import { ENGLISH, ENGLISH_ALLOWED } from './lists/en';
import { foldWord, readView, type View } from './normalize';
import {
  ACTIONS,
  type Action,
  CATEGORIES,
  type Category,
  type Finding,
  type Policy,
  type Verdict,
  type WordList,
} from './types';

interface Term {
  term: string;
  category: Category;
  whole: boolean;
  /** Written with spaces: may run across words even where single words may not. */
  phrase: boolean;
  pattern: RegExp;
}

/**
 * A word as a pattern over folded text: each letter may repeat (`fuuuck`), but never fewer
 * times than the word has it, so a word with a double letter doesn't match one with a single
 * (`nigger` doesn't match `Niger`).
 */
function patternOf(word: string): RegExp | null {
  const folded = foldWord(word);
  if (!folded) return null;
  let source = '';
  for (let i = 0; i < folded.length; ) {
    let j = i;
    while (folded[j] === folded[i]) j++;
    source += j - i === 1 ? `${folded[i]}+` : `${folded[i]}{${j - i},}`;
    i = j;
  }
  return new RegExp(source, 'g');
}

/** Every place `pattern` matches in `text`, overlapping ones too. */
function spans(pattern: RegExp, text: string): [number, number][] {
  const found: [number, number][] = [];
  pattern.lastIndex = 0;
  for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
    found.push([match.index, match.index + match[0].length]);
    pattern.lastIndex = match.index + 1;
  }
  return found;
}

const strictest = (actions: Action[]): Action =>
  actions.reduce<Action>((a, b) => (ACTIONS.indexOf(b) > ACTIONS.indexOf(a) ? b : a), 'allow');

/** The original text with the given UTF-16 ranges hidden, one `*` per character. */
function mask(text: string, ranges: [number, number][]): string {
  let out = '';
  for (let at = 0; at < text.length; ) {
    const char = String.fromCodePoint(text.codePointAt(at) ?? 0);
    out += ranges.some(([start, end]) => at >= start && at < end) ? '*' : char;
    at += char.length;
  }
  return out;
}

export interface TextSafetyOptions {
  /** Word lists, English by default; add one per language. */
  lists?: readonly WordList[];
  /** Innocent words that contain listed ones. */
  allowed?: readonly string[];
}

export interface TextSafety {
  /** Finds harmful words in `text` and says what `policy` does about them. */
  check(text: string, policy: Policy): Verdict;
  /** A checker with more words or exceptions, for a place that needs its own (a game's chat). */
  extend(more: TextSafetyOptions): TextSafety;
}

/**
 * A checker over the given lists. Patterns are built once here, so make one and reuse it;
 * `textSafety` is the shared one with the English lists.
 */
export function createTextSafety(options: TextSafetyOptions = {}): TextSafety {
  const lists = options.lists ?? [ENGLISH];
  const allowed = options.allowed ?? ENGLISH_ALLOWED;

  const terms: Term[] = [];
  for (const list of lists) {
    for (const category of CATEGORIES) {
      const words = list[category];
      for (const [whole, group] of [
        [false, words?.part ?? []],
        [true, words?.word ?? []],
      ] as const) {
        for (const term of group) {
          const pattern = patternOf(term);
          if (pattern) terms.push({ term, category, whole, phrase: term.includes(' '), pattern });
        }
      }
    }
  }
  const exceptions = allowed.map(patternOf).filter((pattern): pattern is RegExp => !!pattern);

  function find(view: View, policy: Policy): Finding[] {
    const { text, wordStart } = view;
    const startsWord = (at: number) => at >= text.length || wordStart[at] === true;
    const innocent = exceptions.flatMap((pattern) => spans(pattern, text));
    const findings: Finding[] = [];
    const seen = new Set<string>();

    for (const { term, category, whole, phrase, pattern } of terms) {
      const action = policy.actions[category];
      if (action === 'allow') continue;
      for (const [start, end] of spans(pattern, text)) {
        if (whole && !(startsWord(start) && startsWord(end))) continue;
        const crosses = wordStart.slice(start + 1, end).includes(true);
        if (!whole && !phrase && !policy.acrossWords && crosses) continue;
        // A phrase in a sentence still starts and ends on whole words (not "classic shooter").
        if (phrase && !policy.acrossWords && !(startsWord(start) && startsWord(end))) continue;
        if (innocent.some(([from, to]) => from <= start && end <= to)) continue;
        const key = `${category}:${start}:${end}`;
        if (seen.has(key)) continue;
        seen.add(key);
        findings.push({
          category,
          term,
          start: view.from[start] ?? 0,
          end: view.to[end - 1] ?? 0,
          action,
        });
      }
    }
    return findings.sort((a, b) => a.start - b.start);
  }

  return {
    check(text, policy) {
      const findings = find(readView(text), policy);
      return {
        action: strictest(findings.map((finding) => finding.action)),
        findings,
        categories: [...new Set(findings.map((finding) => finding.category))],
        masked: mask(
          text,
          findings.map(({ start, end }) => [start, end]),
        ),
      };
    },
    extend(more) {
      return createTextSafety({
        lists: [...lists, ...(more.lists ?? [])],
        allowed: [...allowed, ...(more.allowed ?? [])],
      });
    },
  };
}

/** The shared checker with the English lists. */
export const textSafety: TextSafety = createTextSafety();
